import {
  Braces,
  ChartNoAxesColumn,
  CircleCheck,
  CirclePlay,
  Crosshair,
  FileCode,
  FileText,
  History,
  Layers,
  LayoutTemplate,
  Link2,
  type LucideIcon,
  Package,
  Palette,
  Pencil,
  Rocket,
  Route,
  ScrollText,
  ShoppingCart,
  Shuffle,
  Sparkles,
  SplitSquareHorizontal,
  Target,
} from "lucide-react";

import {
  getPrettyToolName,
  normalizeToolName,
} from "@src/features/ai/utils/tool-constants";

/**
 * A single row in the experience-agent "thought process" timeline: an icon, a
 * short past-tense title of what the step did, and an optional one-line
 * description. For tool steps the title + description are derived here from the
 * tool call's own arguments (`input`) — the FE reads the tool call, so the copy
 * stays in lockstep with what was actually configured.
 */
export type ThoughtStep = {
  Icon: LucideIcon;
  title: string;
  description?: string;
};

// Defensive readers — a tool call's `input` is the model's raw arguments, so
// narrow every access rather than trusting a shape.
const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" ? (v as Record<string, unknown>) : {};
const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asString = (v: unknown): string | undefined =>
  typeof v === "string" && v.length > 0 ? v : undefined;

const capitalize = (s: string): string =>
  s.length ? s[0].toUpperCase() + s.slice(1) : s;

// camelCase key → "Title Case Words" (e.g. netRevenuePerVisitor → "Net Revenue
// Per Visitor"). Keeps the step copy friendly without a per-key lookup table.
const humanizeKey = (key: string): string =>
  capitalize(key.replace(/([a-z0-9])([A-Z])/g, "$1 $2")).replace(/\s+/g, " ");

// Strip protocol + host so a redirect reads as a short path.
const toPath = (u: unknown): string | undefined => {
  const s = asString(u);
  if (!s) return undefined;
  return s.replace(/^https?:\/\/[^/]+/i, "") || s;
};

// Template ids are kebab-case registry keys ("tag-query"); the merchant-facing
// title lives with the template on the backend, so a step reads the id back as
// words rather than duplicating that copy here.
const humanizeTemplateId = (id: string): string =>
  capitalize(id.replace(/[-_]+/g, " "));

const LIFECYCLE_TITLES: Record<string, string> = {
  pause: "Paused the rule",
  resume: "Resumed the rule",
  archive: "Archived the rule",
  restore: "Restored the rule",
  delete: "Deleted the rule",
  rollback: "Rolled the rule back",
};

function describeCreatedRule(input: unknown): string | undefined {
  const rec = asRecord(input);
  const name = asString(rec.name);
  const templateId = asString(rec.templateId);
  const parts = [
    name,
    templateId ? humanizeTemplateId(templateId) : undefined,
  ].filter((part): part is string => !!part);
  return parts.length ? parts.join(" · ") : undefined;
}

function describeRuleDraft(input: unknown): string | undefined {
  const rec = asRecord(input);
  const source = asString(rec.source);
  const parts: string[] = [];
  if (source) {
    const lines = source.split("\n").length;
    parts.push(`${lines} line${lines === 1 ? "" : "s"}`);
  }
  const scenarios = asArray(rec.validationScenarios).length;
  if (scenarios) {
    parts.push(`${scenarios} test scenario${scenarios === 1 ? "" : "s"}`);
  } else if (asString(rec.userTestSuite)) {
    parts.push("with tests");
  }
  return parts.length ? parts.join(" · ") : undefined;
}

function describeVariations(input: unknown): string | undefined {
  const variations = asArray(asRecord(input).finalVariations);
  if (variations.length === 0) return undefined;
  const names = variations
    .map((v) => asString(asRecord(v).name))
    .filter((n): n is string => !!n);
  const percentages = variations
    .map((v) => asRecord(v).percentage)
    .filter((p): p is number => typeof p === "number");
  const namePart = names.join(" + ");
  const splitPart = percentages.length ? `, ${percentages.join("/")}` : "";
  return namePart ? `${namePart}${splitPart}` : undefined;
}

function describeVisitorType(visitorType: unknown): string {
  if (visitorType === "new") return "New visitors";
  if (visitorType === "returning") return "Returning visitors";
  return "All visitors";
}

function describeDevice(device: unknown): string {
  if (device === "mobile") return "mobile";
  if (device === "desktop") return "desktop";
  return "all devices";
}

function describeCommonAudience(input: unknown): string {
  const rec = asRecord(input);
  const visitor = describeVisitorType(rec.visitorType);
  const device = describeDevice(rec.device);
  const extras: string[] = [];
  const countries = asArray(rec.countries).filter(
    (c): c is string => typeof c === "string"
  );
  if (countries.length) extras.push(countries.join("/"));
  const sources = asArray(rec.trafficSources).filter(
    (s): s is string => typeof s === "string"
  );
  if (sources.length) extras.push(sources.join(", "));
  const base = `${visitor}, ${device}`;
  return extras.length ? `${base} · ${extras.join(" · ")}` : base;
}

const TEST_TYPE_LABELS: Record<string, string> = {
  "content/url": "To a URL redirect test",
  "content/template": "To a template test",
  "content/theme": "To a theme test",
  "content/advanced": "To a CSS/JS test",
  "content/onsiteEdits": "To onsite edits",
};

function describePageTargeting(input: unknown): string | undefined {
  const groups = asArray(asRecord(input).conditions);
  const labels: string[] = [];
  for (const group of groups) {
    for (const query of asArray(asRecord(group).queries)) {
      const q = asRecord(query);
      const value = asString(q.value);
      if (q.type === "urlPath" && value) labels.push(value);
      else if (q.type === "jsExpression") labels.push("custom rule");
    }
  }
  const unique = [...new Set(labels)];
  if (unique.length === 0) return undefined;
  return unique.length > 3
    ? `${unique.slice(0, 3).join(", ")} +${unique.length - 3} more`
    : unique.join(", ");
}

function describeProductTargeting(input: unknown): string | undefined {
  const groups = asArray(asRecord(input).conditions);
  const labels: string[] = [];
  for (const group of groups) {
    for (const query of asArray(asRecord(group).queries)) {
      const q = asRecord(query);
      const attribute = asString(q.attribute);
      const count =
        asArray(q.products).length ||
        asArray(q.collections).length ||
        asArray(q.tags).length;
      if (attribute === "productId") labels.push(`${count} products`);
      else if (attribute === "collection") labels.push(`${count} collections`);
      else if (attribute === "tag") labels.push(`${count} tags`);
      else if (attribute === "price") labels.push("price");
      else if (attribute === "inventory") labels.push("inventory");
    }
  }
  const unique = [...new Set(labels)];
  return unique.length ? unique.join(", ") : undefined;
}

function describeRedirects(input: unknown): string | undefined {
  const redirects = asArray(asRecord(input).redirects);
  const treatments = redirects.filter(
    (r) => !asRecord(r).skip && asString(asRecord(r).destinationUrl)
  );
  if (treatments.length === 0) {
    return redirects.length ? `${redirects.length} groups` : undefined;
  }
  const first = asRecord(treatments[0]);
  const origin = toPath(first.originUrl) ?? toPath(first.find);
  const dest = toPath(first.destinationUrl);
  const mapping = origin && dest ? `${origin} → ${dest}` : dest;
  if (!mapping) return `${treatments.length} redirects`;
  return treatments.length > 1
    ? `${mapping} (+${treatments.length - 1} more)`
    : mapping;
}

/**
 * Map a tool call to its timeline step. Falls back to the tool's pretty name
 * with no description for tools without bespoke copy, so a new tool still
 * renders a sensible step.
 */
export function getToolStep(toolName: string, input: unknown): ThoughtStep {
  // An MCP-exposed tool arrives namespaced (`mcp__server__tool`); the step copy
  // keys off the bare tool name so both call paths render the same row.
  switch (normalizeToolName(toolName)) {
    case "setExperienceDetails":
      return {
        Icon: Pencil,
        title: "Named the experience",
        description: asString(asRecord(asRecord(input).experienceDetails).name),
      };
    case "setExperienceMode":
      return {
        Icon: SplitSquareHorizontal,
        title:
          asRecord(input).mode === "personalization"
            ? "Set up as a personalization"
            : "Set up as an A/B test",
      };
    case "setVariations": {
      // A personalization is a single 100% treatment; a test is a control +
      // variant(s) split. Infer from the variation count so the copy matches.
      const variations = asArray(asRecord(input).finalVariations);
      if (variations.length === 1) {
        return {
          Icon: Braces,
          title: "Set up the personalization",
          description: asString(asRecord(variations[0]).name),
        };
      }
      return {
        Icon: Braces,
        title: "Created test groups",
        description: describeVariations(input),
      };
    }
    case "setUrlRedirects":
      return {
        Icon: Shuffle,
        title: "Set up the redirect",
        description: describeRedirects(input),
      };
    case "setTemplateRedirects":
      return {
        Icon: Shuffle,
        title: "Set up the template test",
        description: (() => {
          const n = asArray(asRecord(input).redirects).length;
          return n ? `${n} template variations` : undefined;
        })(),
      };
    case "setThemeRedirects":
      return {
        Icon: Shuffle,
        title: "Set up the theme test",
        description: (() => {
          const n = asArray(asRecord(input).redirects).length;
          return n ? `${n} theme variations` : undefined;
        })(),
      };
    case "switchTestType": {
      const testType = asString(asRecord(input).testType);
      return {
        Icon: Shuffle,
        title: "Switched the test type",
        description: testType
          ? (TEST_TYPE_LABELS[testType] ?? testType)
          : undefined,
      };
    }
    case "setCommonAudience":
      return {
        Icon: Target,
        title: "Set up targeting",
        description: describeCommonAudience(input),
      };
    case "setCustomAudience":
      return {
        Icon: Target,
        title: "Set up targeting",
        description: "Custom targeting rules",
      };
    case "setAdvancedAudience":
      return {
        Icon: Target,
        title: "Set up targeting",
        description: "Advanced (JS) targeting",
      };
    case "setCurrencyTargeting": {
      const currencies = asArray(asRecord(input).currencies).filter(
        (c): c is string => typeof c === "string"
      );
      return {
        Icon: Target,
        title: "Set currency targeting",
        description: currencies.length ? currencies.join(", ") : undefined,
      };
    }
    case "setPageTargeting":
      return {
        Icon: Crosshair,
        title: "Limited the pages",
        description: describePageTargeting(input),
      };
    case "setProductTargeting":
      return {
        Icon: Package,
        title: "Limited the products",
        description: describeProductTargeting(input),
      };
    case "setKeyMetrics": {
      const primary = asString(asRecord(input).primaryMetric);
      const secondary = asArray(asRecord(input).standardEventList).length;
      const extra = secondary > 1 ? ` (+${secondary - 1} more)` : "";
      return {
        Icon: ChartNoAxesColumn,
        title: "Chose the metrics",
        description: primary ? `${humanizeKey(primary)}${extra}` : undefined,
      };
    }
    case "setCustomMetric": {
      const ids = asArray(asRecord(input).selectedCustomEventIds).length;
      const opensDrawer = asRecord(input).openCustomEventsDrawer === true;
      return {
        Icon: ChartNoAxesColumn,
        title: opensDrawer
          ? "Opened custom event setup"
          : "Added custom metrics",
        description: ids
          ? `${ids} custom metric${ids === 1 ? "" : "s"}`
          : undefined,
      };
    }
    case "setExclusionGroup":
      return {
        Icon: Route,
        title: "Set up mutual exclusion",
        description: "Won't overlap with the grouped experiences",
      };
    case "setAnalyticsViewType": {
      const onlyProducts = asRecord(input).mode === "only_certain_products";
      const count = asArray(asRecord(input).productIds).length;
      return {
        Icon: Package,
        title: onlyProducts
          ? "Counting orders with certain products"
          : "Counting all orders",
        description:
          onlyProducts && count
            ? `${count} product${count === 1 ? "" : "s"}`
            : undefined,
      };
    }
    case "handoffToVisualEditor":
      // Sole redirect notice — the assistant no longer announces the hand-off
      // in text (see the system prompt). Present-tense so it reads as "you're
      // about to be moved", and the hand-off waits a beat before navigating so
      // this stays readable.
      return {
        Icon: Palette,
        title: "Connecting you to the Visual Builder…",
      };
    case "getHtml":
      return {
        Icon: FileText,
        title: "Read the page",
        description: asString(asRecord(input).url),
      };
    case "searchKnowledgeBase":
      // The raw query is a keyword blob — not presentable; title alone reads
      // cleaner.
      return { Icon: FileText, title: "Searched the knowledge base" };
    case "readKnowledgeDocument":
      return { Icon: FileText, title: "Read a document" };
    // Agentic Logic (Atlas) — rule authoring reads as a timeline of what was
    // built, not as raw snake_case tool names.
    case "create_agentic_logic_rule":
      return {
        Icon: Sparkles,
        title:
          asRecord(input).managedBy === "code"
            ? "Created a custom-code rule"
            : "Created the rule from a template",
        description: describeCreatedRule(input),
      };
    case "save_agentic_logic_rule_draft":
      return {
        Icon: FileCode,
        title: "Wrote the rule logic",
        description: describeRuleDraft(input),
      };
    case "update_agentic_logic_rule":
      return {
        Icon: Pencil,
        title: "Updated the rule",
        description: asString(asRecord(input).name),
      };
    case "validate_agentic_logic_template": {
      const templateId = asString(asRecord(input).templateId);
      return {
        Icon: CircleCheck,
        title: "Checked the rule settings",
        description: templateId ? humanizeTemplateId(templateId) : undefined,
      };
    }
    case "list_agentic_logic_templates":
      return { Icon: LayoutTemplate, title: "Reviewed the rule templates" };
    case "evaluate_agentic_logic_rule":
      return { Icon: CirclePlay, title: "Test-ran the rule" };
    case "evaluate_agentic_logic_rule_scenario": {
      const name = asString(asRecord(asRecord(input).scenario).name);
      return {
        Icon: CirclePlay,
        title: "Ran a cart against the rule",
        description: name,
      };
    }
    case "list_agentic_logic_rule_scenarios":
      return { Icon: ShoppingCart, title: "Looked up the example carts" };
    case "publish_agentic_logic_rule":
      return { Icon: Rocket, title: "Published the rule" };
    case "get_agentic_logic_publish_job":
      return { Icon: Rocket, title: "Checked the publish status" };
    case "agentic_logic_rule_lifecycle": {
      const action = asString(asRecord(input).action);
      return {
        Icon: History,
        title:
          (action ? LIFECYCLE_TITLES[action] : undefined) ??
          "Updated the rule status",
        description:
          action === "rollback" && typeof asRecord(input).version === "number"
            ? `To version ${String(asRecord(input).version)}`
            : undefined,
      };
    }
    case "list_agentic_logic_rule_versions":
      return { Icon: History, title: "Reviewed the rule versions" };
    case "list_agentic_logic_rules":
      return { Icon: ScrollText, title: "Reviewed the existing rules" };
    case "get_agentic_logic_rule":
    case "get_agentic_logic_rule_draft":
      return { Icon: ScrollText, title: "Read the rule" };
    case "create_agentic_logic_placement":
      return {
        Icon: Layers,
        title: "Created a placement",
        description: asString(asRecord(input).name),
      };
    case "list_agentic_logic_placements":
      return { Icon: Layers, title: "Reviewed the placements" };
    case "create_agentic_logic_placement_assignment":
      return { Icon: Link2, title: "Assigned the rule to a placement" };
    case "update_agentic_logic_placement_assignment":
      return { Icon: Link2, title: "Updated the placement assignment" };
    case "delete_agentic_logic_placement_assignment":
      return { Icon: Link2, title: "Removed the rule from a placement" };
    case "list_agentic_logic_placement_assignments":
      return { Icon: Link2, title: "Reviewed the placement assignments" };
    default:
      return { Icon: Braces, title: getPrettyToolName(toolName) };
  }
}
